import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Nav,
  Navbar,
  Row,
  Spinner,
  InputGroup,
  ProgressBar,
  Carousel,
} from "react-bootstrap";

/**
 * CERTIFIED_SOLAR_REPORT_Landing_v01A_WIZARD (React + TypeScript + React-Bootstrap)
 * Version: v01A
 * Date: 2025-08-30 (America/Denver)
 *
 * Highlights (mirrors your framework):
 * - Wizard/Vertical toggle with progress.
 * - On-blur validation helpers (vp / vpOpt) + green success styling.
 * - Modular submit pipeline: buildLeadPayload -> toWirePayloadStrings -> postLead.
 * - Section components reused across Wizard and Vertical (no duplication).
 * - Sectioned confirmation summary (no JSON dump).
 */

/* ============================================================
   CONFIG
============================================================ */
const VERSION = "v01A" as const;
const SHOW_VERSION = false;
const TEST_MODE = false; // true => logs payload, skips network
const API_URL = "https://wi8zkeven2.execute-api.us-east-1.amazonaws.com/prod/feedback";
const AGENT_PHONE_NUMBER = "(303) 337-4433";
const imagePath = "solar/images/HOME-SOLAR/"; // adjust to your asset folder

/* ============================================================
   TYPES
============================================================ */
interface ISolarLeadPayload {
  // Contact
  fullName: string;
  email: string;
  phone: string; // 10-digit digits-only

  // Address
  address1: string;
  address2?: string;
  city: string;
  state: string; // 2-letter
  zipCode: string; // 5-digit

  // Energy profile
  monthlyUtilityBill: string; // numeric string dollars
  hasPool: boolean;
  hasHotTub: boolean;
  hasEVs: boolean;
  hasOtherHighUse: boolean;
  otherHighUseNotes?: string;

  // Meta
  domainName: string;
  itemCategory: string; // e.g., Customer.solar.report
  itemClass: string; // report.request
  itemType: string; // solar.certified.home.report
  clientVersion: string;
  utm?: { source?: string; medium?: string; campaign?: string };
}

/** Wire payload where EVERY VALUE IS A STRING */
type WirePayload = Record<string, string>;

/* ============================================================
   HELPERS
============================================================ */
const US_STATES: { code: string; name: string }[] = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" },
];

function digitsOnly(s: string): string { return (s ?? "").replace(/\D+/g, ""); }
function isValidEmail(email: string): boolean { return /.+@.+\..+/.test(email); }
function isValidPhone(raw: string): boolean { return digitsOnly(raw).length === 10; }
function isValidZipCode(raw: string): boolean { return digitsOnly(raw).length === 5; }
function formatPhonePretty(raw: string): string {
  const d = digitsOnly(raw);
  if (d.length !== 10) return raw || "—";
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}
const dash = (v?: string | number | boolean) => (v === undefined || v === null || (typeof v === "string" && v.trim().length === 0)) ? "—" : String(v);
const money = (nLike?: string) => {
  const digits = (nLike ?? "").replace(/[^\d.]/g, "");
  const n = Number(digits || "0");
  if (!Number.isFinite(n) || n <= 0) return "—";
  try { return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 }); }
  catch { return `$${n}`; }
};

/* ============================================================
   SLIDES
============================================================ */
interface Slide { src: string; alt: string; title: string; subline: string; smallCaption: string; }
const slides: Slide[] = [
  {
    src: imagePath + "AdobeStock_309530595_SolarRoof_Preview.jpg",
    alt: "Modern home with rooftop solar in sunlight.",
    title: "Certified Home Solar Report",
    subline: "See savings, system size, and timelines—no obligation.",
    smallCaption: "Clarity before you buy.",
  },
  {
    src: imagePath + "AdobeStock_220913807_ElectricBill_Preview.jpg",
    alt: "Utility bill with calculator.",
    title: "Slash Your Electric Bill",
    subline: "Understand your break-even point and incentives.",
    smallCaption: "Numbers you can trust.",
  },
  {
    src: imagePath + "AdobeStock_380927401_EVHome_Preview.jpg",
    alt: "EV charging in a garage with solar inverter.",
    title: "EV & High-Use Ready",
    subline: "Pool? Hot tub? EVs? We factor it all in.",
    smallCaption: "Right-size your system.",
  },
];

/* ============================================================
   MAIN
============================================================ */
const steps = ["Contact & Address", "Energy Profile", "Confirmation"] as const;

export const CERTIFIED_SOLAR_REPORT_Landing_v02O_WIZARD: React.FC = () => {
  const formRef = useRef<HTMLDivElement | null>(null);
  const [activeSlide, setActiveSlide] = useState(0);

  // Contact
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  // Address
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");

  // Energy profile
  const [monthlyUtilityBill, setMonthlyUtilityBill] = useState<string>("");
  const [hasPool, setHasPool] = useState<boolean>(false);
  const [hasHotTub, setHasHotTub] = useState<boolean>(false);
  const [hasEVs, setHasEVs] = useState<boolean>(false);
  const [hasOtherHighUse, setHasOtherHighUse] = useState<boolean>(false);
  const [otherHighUseNotes, setOtherHighUseNotes] = useState<string>("");

  // Submit / server state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitOk, setSubmitOk] = useState(false);
  const [serverStatus, setServerStatus] = useState<null | { ok: boolean; status?: number; id?: string; message?: string }>(null);
  const [lastSubmittedApp, setLastSubmittedApp] = useState<ISolarLeadPayload | null>(null);

  // Mode + wizard
  const [mode, setMode] = useState<"wizard" | "vertical">("wizard");
  const isWizard = mode === "wizard";
  const [currentStep, setCurrentStep] = useState(0);

  /* ---------- Touched + validation ---------- */
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const isTouched = (name: string) => !!touched[name];
  const markTouched = (name: string) => setTouched((t) => ({ ...t, [name]: true }));

  const vp = (name: string, pass: boolean) => ({
    onBlur: () => markTouched(name),
    isValid: isTouched(name) && pass,
    isInvalid: isTouched(name) && !pass,
    className: isTouched(name) && pass ? "bg-success-subtle" : undefined,
  });
  const vpOpt = (name: string, valuePresent: boolean, passIfPresent: boolean = true) => ({
    onBlur: () => markTouched(name),
    isValid: isTouched(name) && valuePresent && passIfPresent,
    isInvalid: isTouched(name) && valuePresent && !passIfPresent,
    className: isTouched(name) && valuePresent && passIfPresent ? "bg-success-subtle" : undefined,
  });

  // Validators
  const V = {
    fullName: fullName.trim().length >= 2,
    email: isValidEmail(email),
    phone: isValidPhone(phone),

    address1: address1.trim().length >= 3,
    city: city.trim().length >= 2,
    state: state !== "",
    zip: isValidZipCode(zipCode),

    monthlyUtilityBill: Number(digitsOnly(monthlyUtilityBill)) > 0,
    otherHighUseNotes: otherHighUseNotes.trim().length === 0 || otherHighUseNotes.trim().length >= 2,
  };

  const valid = useMemo(() => (
    V.fullName && V.email && V.phone && V.address1 && V.city && V.state && V.zip && V.monthlyUtilityBill && V.otherHighUseNotes
  ), [V]);

  function isStepValid(stepIdx: number): boolean {
    switch (stepIdx) {
      case 0:
        return V.fullName && V.email && V.phone && V.address1 && V.city && V.state && V.zip;
      case 1:
        return V.monthlyUtilityBill && V.otherHighUseNotes;
      case 2:
        return submitOk === true;
      default:
        return false;
    }
  }

  const goNext = () => {
    const touchMany = (names: string[]) => setTouched((t) => names.reduce((acc, n) => ({ ...acc, [n]: true }), t));
    if (currentStep === 0) touchMany(["fullName","email","phone","address1","city","state","zip"]);
    else if (currentStep === 1) touchMany(["monthlyUtilityBill","otherHighUseNotes"]);
    if (!isStepValid(currentStep)) return;
    setCurrentStep((s) => Math.min(2, s + 1));
  };
  const goBack = () => setCurrentStep((s) => Math.max(0, s - 1));

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  /* ============================================================
     PAYLOAD & SUBMIT
  ============================================================ */
  function buildLeadPayload(): ISolarLeadPayload {
    return {
      fullName: fullName.trim(),
      email: email.trim(),
      phone: digitsOnly(phone),

      address1: address1.trim(),
      address2: address2?.trim() || "",
      city: city.trim(),
      state: String(state),
      zipCode: String(zipCode),

      monthlyUtilityBill: String(Number(digitsOnly(monthlyUtilityBill))),
      hasPool,
      hasHotTub,
      hasEVs,
      hasOtherHighUse,
      otherHighUseNotes: otherHighUseNotes?.trim() || "",

      domainName: typeof window !== "undefined" ? String(window.location.hostname) : "unknown",
      itemCategory: "Customer.solar.report",
      itemClass: "report.request",
      itemType: "solar.certified.home.report",
      clientVersion: String(VERSION),
    };
  }

  function toWirePayloadStrings(p: ISolarLeadPayload): WirePayload {
    const out: WirePayload = {};
    for (const [k, v] of Object.entries(p)) {
      if (typeof v === "string") out[k] = v;
      else if (typeof v === "number") out[k] = Number.isFinite(v) ? `${v}` : "";
      else if (typeof v === "boolean") out[k] = v ? "true" : "false";
      else if (Array.isArray(v)) { try { out[k] = JSON.stringify(v); } catch { out[k] = ""; } }
      else if (v && typeof v === "object") { try { out[k] = JSON.stringify(v); } catch { out[k] = ""; } }
      else out[k] = "";
    }
    return out;
  }

  async function postLead(payload: WirePayload): Promise<{ ok: boolean; status: number; id?: string; message?: string }> {
    const res = await fetch(API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      mode: "cors",
    });

    let resJson: unknown = null;
    try { const txt = await res.text(); resJson = txt ? JSON.parse(txt) : null; } catch { resJson = null; }
    const asObj = (x: unknown): Record<string, unknown> | null => x && typeof x === "object" && !Array.isArray(x) ? (x as Record<string, unknown>) : null;

    if (!res.ok) {
      const o = asObj(resJson); const msg = (o?.error as string) || (o?.message as string) || `Request failed: ${res.status}`;
      return { ok: false, status: res.status, message: msg };
    }
    const o = asObj(resJson); const id = (o?.id as string) || (o?.requestId as string) || undefined;
    return { ok: true, status: res.status, id };
  }

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();

    // Only submit on last data step in Wizard; otherwise advance.
    const lastDataStepIdx = 1;
    if (isWizard && currentStep < lastDataStepIdx) { goNext(); return; }

    setSubmitError(null); setSubmitOk(false); setServerStatus(null);

    // Mark all required fields as touched for final validation
    setTouched((t) => ({
      ...t,
      fullName: true, email: true, phone: true,
      address1: true, city: true, state: true, zip: true,
      monthlyUtilityBill: true, otherHighUseNotes: true,
    }));

    if (!valid) { setSubmitError("Please complete all required fields."); return; }

    const appPayload = buildLeadPayload();
    const wirePayload = toWirePayloadStrings(appPayload);

    try {
      setSubmitting(true);
      if (TEST_MODE) {
        console.log("[TEST_MODE] Submitting payload (wire):", wirePayload);
        await new Promise((r) => setTimeout(r, 600));
        const status = { ok: true, status: 200, id: "TEST-" + Math.random().toString(36).slice(2, 8) };
        setServerStatus(status); setLastSubmittedApp(appPayload); setSubmitOk(true);
        if (isWizard) setCurrentStep(2);
        return;
      }

      const status = await postLead(wirePayload);
      setServerStatus(status);
      if (!status.ok) throw new Error(status.message || "Unknown server error");
      setLastSubmittedApp(appPayload); setSubmitOk(true);
      if (isWizard) setCurrentStep(2);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setSubmitError(msg); setSubmitOk(false);
    } finally { setSubmitting(false); }
  };

  // Clamp active slide index
  const active = slides[Math.min(slides.length - 1, Math.max(0, Number.isFinite(activeSlide) ? activeSlide : 0))];

  /* ------------------------------------------------------------
     UI
  ------------------------------------------------------------ */
  return (
    <div className="bg-body">
      {/* Sticky Topbar */}
      <Navbar expand="md" bg="light" className="border-bottom sticky-top">
        <Container>
          <Navbar.Brand href="#" className="fw-bold">
            Certified <span className="text-primary">Home Solar</span> Report
            {SHOW_VERSION && <Badge bg="secondary" className="ms-2">{VERSION}</Badge>}
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="topbar" />
          <Navbar.Collapse id="topbar" className="justify-content-end">
            <Nav className="align-items-center gap-2">
              <div className="d-none d-md-block text-body-secondary me-2">Speak with an advisor:</div>
              <Button variant="outline-primary" href="tel:3033374433">{AGENT_PHONE_NUMBER}</Button>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* HERO */}
      <section id="home" className="position-relative overflow-hidden" style={{ minHeight: "760px" }}>
        <div className="position-absolute top-0 start-0 end-0 bottom-0" style={{ zIndex: 0 }} aria-hidden>
          <Carousel
            activeIndex={activeSlide}
            onSelect={(i) => setActiveSlide(typeof i === "number" ? i : 0)}
            controls
            indicators
            fade
            interval={3500}
            className="w-100 h-100"
          >
            {slides.map((s, idx) => (
              <Carousel.Item key={idx} className="w-100 h-100">
                <div
                  role="img"
                  aria-label={s.alt}
                  style={{
                    width: "100%",
                    height: "100%",
                    minHeight: 760,
                    backgroundImage: `linear-gradient(to right, rgba(0,0,0,0.55), rgba(0,0,0,0.25) 40%, rgba(0,0,0,0.10) 60%, rgba(0,0,0,0.00)), url('${s.src}')`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
                <Carousel.Caption className="text-start" style={{ bottom: "40%" }} />
              </Carousel.Item>
            ))}
          </Carousel>
        </div>

        {/* Foreground content */}
        <Container className="position-relative" style={{ zIndex: 1 }}>
          <Row className="align-items-start g-4 p-5">
            <Col lg={6} className="text-white">
              <div className="pe-lg-4">
                <h1 className="display-5 fw-bold" style={{ marginTop: "400px" }}>{active.title}</h1>
                <p className="lead mb-3">{active.subline}</p>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <Badge bg="light" text="dark">Incentives & ROI</Badge>
                  <Badge bg="light" text="dark">EV / Pool / Hot Tub Ready</Badge>
                  <Badge bg="light" text="dark">Licensed Advisors</Badge>
                </div>
                <div className="mt-4 d-flex gap-2">
                  <Button variant="outline-light" onClick={scrollToForm}>Start My Report</Button>
                </div>
              </div>
            </Col>

            <Col lg={6}>
              <Card className="shadow-lg" style={{ backdropFilter: "blur(6px)" }}>
                <Card.Header className="bg-white">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <div className="fw-semibold">Get Your Certified Home Solar Report</div>
                      <small className="text-body-secondary">We’ll estimate savings, size, and next steps.</small>
                    </div>
                    <Badge bg="success">Secure</Badge>
                  </div>
                </Card.Header>
                <Card.Body ref={formRef}>
                  {submitError && (
                    <Alert variant="danger" onClose={() => setSubmitError(null)} dismissible>
                      Server: {submitError}
                    </Alert>
                  )}

                  {/* MODE TOGGLE + (Wizard) Stepper */}
                  <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <Form.Check
                        type="switch"
                        id="modeSwitch"
                        label={isWizard ? "Wizard mode" : "Vertical mode"}
                        checked={isWizard}
                        onChange={(e) => {
                          setMode(e.currentTarget.checked ? "wizard" : "vertical");
                          setTouched({});
                          if (submitOk && e.currentTarget.checked) setCurrentStep(2); else setCurrentStep(0);
                        }}
                      />
                      <small className="text-muted">Switch to see all fields in one view.</small>
                    </div>

                    <div style={{ minWidth: 280 }}>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <Badge bg="secondary">Step {isWizard ? currentStep + 1 : (submitOk ? 2 : 1)} of {isWizard ? 3 : (submitOk ? 2 : 1)}</Badge>
                        <small className="text-muted">{isWizard ? steps[currentStep] : (submitOk ? "Confirmation" : "All fields")}</small>
                      </div>
                      <ProgressBar now={isWizard ? ((currentStep + 1) / 3) * 100 : (submitOk ? 100 : 50)} />
                    </div>
                  </div>

                  {/* FORM */}
                  <Form
                    onSubmit={handleSubmit}
                    noValidate
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && isWizard) {
                        // Prevent premature submit; advance when valid.
                        if (currentStep < 1) { e.preventDefault(); e.stopPropagation(); if (isStepValid(currentStep)) goNext(); }
                      }
                    }}
                  >
                    {isWizard ? (
                      <>
                        <Row className="g-3">
                          {currentStep === 0 && (
                            <Col xs={12}>
                              <SectionCard title="Contact & Address">
                                <SectionContactAddress
                                  values={{ fullName, email, phone, address1, address2, city, state, zipCode }}
                                  setters={{ setFullName, setEmail, setPhone, setAddress1, setAddress2, setCity, setState, setZipCode }}
                                  V={V}
                                  vp={vp}
                                  vpOpt={vpOpt}
                                />
                              </SectionCard>
                            </Col>
                          )}

                          {currentStep === 1 && (
                            <Col xs={12}>
                              <SectionCard title="Energy Profile">
                                <SectionEnergyProfile
                                  values={{ monthlyUtilityBill, hasPool, hasHotTub, hasEVs, hasOtherHighUse, otherHighUseNotes }}
                                  setters={{ setMonthlyUtilityBill, setHasPool, setHasHotTub, setHasEVs, setHasOtherHighUse, setOtherHighUseNotes }}
                                  V={V}
                                  vp={vp}
                                  vpOpt={vpOpt}
                                />
                              </SectionCard>
                            </Col>
                          )}

                          {currentStep === 2 && (
                            <Col xs={12}>
                              <ConfirmationBlock status={serverStatus} app={lastSubmittedApp} />
                            </Col>
                          )}
                        </Row>

                        {/* Wizard controls */}
                        {currentStep < 2 && (
                          <div className="d-flex justify-content-between mt-3">
                            <Button
                              type="button"
                              variant="outline-secondary"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); goBack(); }}
                              disabled={currentStep === 0}
                            >
                              Previous
                            </Button>

                            {currentStep < 1 ? (
                              <Button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (isStepValid(currentStep)) goNext(); }} disabled={!isStepValid(currentStep)}>
                                Next
                              </Button>
                            ) : (
                              <Button type="submit" variant="primary" size="lg" disabled={submitting || !valid}>
                                {submitting ? (<><Spinner animation="border" size="sm" className="me-2" /> Submitting...</>) : (<>Get My Report</>)}
                              </Button>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      // VERTICAL VIEW (reuse same sections)
                      <>
                        {!submitOk ? (
                          <>
                            <Row className="g-3">
                              <Col xs={12}>
                                <SectionCard title="Contact & Address">
                                  <SectionContactAddress
                                    values={{ fullName, email, phone, address1, address2, city, state, zipCode }}
                                    setters={{ setFullName, setEmail, setPhone, setAddress1, setAddress2, setCity, setState, setZipCode }}
                                    V={V}
                                    vp={vp}
                                    vpOpt={vpOpt}
                                  />
                                </SectionCard>
                              </Col>

                              <Col xs={12}>
                                <SectionCard title="Energy Profile">
                                  <SectionEnergyProfile
                                    values={{ monthlyUtilityBill, hasPool, hasHotTub, hasEVs, hasOtherHighUse, otherHighUseNotes }}
                                    setters={{ setMonthlyUtilityBill, setHasPool, setHasHotTub, setHasEVs, setHasOtherHighUse, setOtherHighUseNotes }}
                                    V={V}
                                    vp={vp}
                                    vpOpt={vpOpt}
                                  />
                                </SectionCard>
                              </Col>
                            </Row>

                            <div className="d-grid mt-3">
                              <Button type="submit" variant="primary" size="lg" disabled={!valid || submitting}>
                                {submitting ? (<><Spinner animation="border" size="sm" className="me-2" /> Submitting...</>) : (<>Get My Report</>)}
                              </Button>
                              <div className="text-center mt-2 text-body-secondary" style={{ fontSize: "0.9rem" }}>
                                By clicking, you agree we may contact you about solar options. No spam. You can opt out anytime.
                              </div>
                            </div>
                          </>
                        ) : (
                          <ConfirmationBlock status={serverStatus} app={lastSubmittedApp} />
                        )}
                      </>
                    )}
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* How It Works */}
      <section className="py-5 bg-light border-top border-bottom">
        <Container>
          <Row className="mb-4">
            <Col lg={8}>
              <h2 className="fw-bold mb-1">How It Works</h2>
              <p className="text-body-secondary mb-0">Three simple steps to your certified solar report.</p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={4}>
              <div className="d-flex align-items-start gap-3">
                <div className="badge rounded-pill text-bg-primary fs-6 mt-1">1</div>
                <div>
                  <h6 className="mb-1">Tell us about your home</h6>
                  <p className="mb-0 text-body-secondary">Contact, address, and electric bill.</p>
                </div>
              </div>
            </Col>
            <Col md={4}>
              <div className="d-flex align-items-start gap-3">
                <div className="badge rounded-pill text-bg-primary fs-6 mt-1">2</div>
                <div>
                  <h6 className="mb-1">Share usage drivers</h6>
                  <p className="mb-0 text-body-secondary">Pool, hot tub, EVs, and other high-usage items.</p>
                </div>
              </div>
            </Col>
            <Col md={4}>
              <div className="d-flex align-items-start gap-3">
                <div className="badge rounded-pill text-bg-primary fs-6 mt-1">3</div>
                <div>
                  <h6 className="mb-1">Get your report</h6>
                  <p className="mb-0 text-body-secondary">Estimated savings, recommended size, and next steps.</p>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Footer */}
      <footer className="py-4">
        <Container>
          <Row className="align-items-center">
            <Col md={6} className="small text-body-secondary">
              © {new Date().getFullYear()} Certified Home Solar Report. All rights reserved.
              <div className="mt-2">This page is for general information and not legal or financial advice.</div>
            </Col>
            <Col md={6} className="d-flex justify-content-md-end mt-3 mt-md-0 gap-2">
              <Button variant="secondary" size="sm" href="tel:3033374433">Call {AGENT_PHONE_NUMBER}</Button>
            </Col>
          </Row>
        </Container>
      </footer>
    </div>
  );
};

/* ============================================================
   REUSABLE SECTION COMPONENTS
============================================================ */

const SectionCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <fieldset className="border rounded-3 p-3 bg-body-secondary">
    <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
      <strong>{title}</strong>
    </legend>
    <div className="mt-1">{children}</div>
  </fieldset>
);

/* ---------- Contact & Address ---------- */
const SectionContactAddress: React.FC<{
  values: { fullName: string; email: string; phone: string; address1: string; address2: string; city: string; state: string; zipCode: string };
  setters: { setFullName: (v: string) => void; setEmail: (v: string) => void; setPhone: (v: string) => void; setAddress1: (v: string) => void; setAddress2: (v: string) => void; setCity: (v: string) => void; setState: (v: string) => void; setZipCode: (v: string) => void };
  V: any;
  vp: (name: string, pass: boolean) => any;
  vpOpt: (name: string, valuePresent: boolean, passIfPresent?: boolean) => any;
}> = ({ values, setters, V, vp, vpOpt }) => {
  const { fullName, email, phone, address1, address2, city, state, zipCode } = values;
  const { setFullName, setEmail, setPhone, setAddress1, setAddress2, setCity, setState, setZipCode } = setters;

  return (
    <Row className="g-3">
      <Col md={6}>
        <Form.Label htmlFor="fullName">Full Name</Form.Label>
        <Form.Control id="fullName" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Jane Doe" required {...vp("fullName", V.fullName)} />
        <Form.Control.Feedback type="invalid">Please enter your full name.</Form.Control.Feedback>
      </Col>
      <Col md={3}>
        <Form.Label htmlFor="phone">Phone</Form.Label>
        <Form.Control id="phone" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 123-4567" required {...vp("phone", V.phone)} />
        <Form.Control.Feedback type="invalid">Enter a 10-digit phone.</Form.Control.Feedback>
      </Col>
      <Col md={3}>
        <Form.Label htmlFor="email">Email</Form.Label>
        <Form.Control id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required {...vp("email", V.email)} />
        <Form.Control.Feedback type="invalid">Enter a valid email.</Form.Control.Feedback>
      </Col>

      <Col md={8}>
        <Form.Label htmlFor="address1">Street Address</Form.Label>
        <Form.Control id="address1" value={address1} onChange={(e) => setAddress1(e.target.value)} placeholder="123 Main St" required {...vp("address1", V.address1)} />
        <Form.Control.Feedback type="invalid">Enter your street address.</Form.Control.Feedback>
      </Col>
      <Col md={4}>
        <Form.Label htmlFor="address2">Unit / Suite (optional)</Form.Label>
        <Form.Control id="address2" value={address2} onChange={(e) => setAddress2(e.target.value)} placeholder="Suite 200" {...vpOpt("address2", !!address2.trim())} />
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="city">City</Form.Label>
        <Form.Control id="city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" required {...vp("city", V.city)} />
      </Col>
      <Col md={4}>
        <Form.Label htmlFor="state">State</Form.Label>
        <Form.Select id="state" value={state} onChange={(e) => setState(e.target.value)} required {...vp("state", V.state)}>
          <option value="">Select</option>
          {US_STATES.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
        </Form.Select>
      </Col>
      <Col md={4}>
        <Form.Label htmlFor="zipCode">Zip</Form.Label>
        <Form.Control id="zipCode" inputMode="numeric" value={zipCode} onChange={(e) => setZipCode(e.target.value)} placeholder="#####" required {...vp("zip", V.zip)} />
        <Form.Control.Feedback type="invalid">Enter a 5-digit zip code.</Form.Control.Feedback>
      </Col>
    </Row>
  );
};

/* ---------- Energy Profile ---------- */
const SectionEnergyProfile: React.FC<{
  values: { monthlyUtilityBill: string; hasPool: boolean; hasHotTub: boolean; hasEVs: boolean; hasOtherHighUse: boolean; otherHighUseNotes: string };
  setters: { setMonthlyUtilityBill: (v: string) => void; setHasPool: (v: boolean) => void; setHasHotTub: (v: boolean) => void; setHasEVs: (v: boolean) => void; setHasOtherHighUse: (v: boolean) => void; setOtherHighUseNotes: (v: string) => void };
  V: any;
  vp: (name: string, pass: boolean) => any;
  vpOpt: (name: string, valuePresent: boolean, passIfPresent?: boolean) => any;
}> = ({ values, setters, V, vp, vpOpt }) => {
  const { monthlyUtilityBill, hasPool, hasHotTub, hasEVs, hasOtherHighUse, otherHighUseNotes } = values;
  const { setMonthlyUtilityBill, setHasPool, setHasHotTub, setHasEVs, setHasOtherHighUse, setOtherHighUseNotes } = setters;

  return (
    <Row className="g-3">
      <Col md={4}>
        <Form.Label htmlFor="bill">Avg. Monthly Electric Bill</Form.Label>
        <InputGroup hasValidation>
          <InputGroup.Text>$</InputGroup.Text>
          <Form.Control id="bill" inputMode="numeric" value={monthlyUtilityBill} onChange={(e) => setMonthlyUtilityBill(e.target.value)} placeholder="e.g., 180" required {...vp("monthlyUtilityBill", V.monthlyUtilityBill)} />
        </InputGroup>
      </Col>

      <Col xs={12} className="pt-2">
        <Form.Label>High-Usage Items</Form.Label>
        <div className="d-flex flex-wrap gap-3">
          <Form.Check id="pool" type="checkbox" label="Pool" checked={hasPool} onChange={(e) => setHasPool(e.currentTarget.checked)} />
          <Form.Check id="hotTub" type="checkbox" label="Hot tub" checked={hasHotTub} onChange={(e) => setHasHotTub(e.currentTarget.checked)} />
          <Form.Check id="evs" type="checkbox" label="EV(s)" checked={hasEVs} onChange={(e) => setHasEVs(e.currentTarget.checked)} />
          <Form.Check id="otherHighUse" type="checkbox" label="Other high-usage" checked={hasOtherHighUse} onChange={(e) => setHasOtherHighUse(e.currentTarget.checked)} />
        </div>
      </Col>

      <Col xs={12}>
        <Form.Label htmlFor="notes">Other High-Usage Details (optional)</Form.Label>
        <Form.Control id="notes" as="textarea" rows={2} value={otherHighUseNotes} onChange={(e) => setOtherHighUseNotes(e.target.value)} placeholder="e.g., sauna, workshop tools, server racks, well pump, etc." {...vpOpt("otherHighUseNotes", !!otherHighUseNotes.trim(), V.otherHighUseNotes)} />
      </Col>
    </Row>
  );
};

/* ============================================================
   Confirmation block (Sectioned, readable, no JSON)
============================================================ */
const ConfirmationBlock: React.FC<{
  status: { ok: boolean; status?: number; id?: string; message?: string } | null;
  app: ISolarLeadPayload | null;
}> = ({ status, app }) => {
  const ok = !!status?.ok;

  if (!app) {
    return (
      <Card className="border-0">
        <Card.Body className="p-4">
          <Alert variant="warning" className="mb-0">We don’t have your details to display yet.</Alert>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="border-0">
      <Card.Body className="p-4">
        {ok ? (
          <Alert variant="success" className="d-flex align-items-start">
            <div>
              <div className="fw-bold mb-1">Thank you! A licensed advisor will contact you shortly.</div>
              <div className="small">Submission status: <strong>{status?.status ?? 200}</strong>{status?.id ? <> • Reference ID: <strong>{status.id}</strong></> : null}</div>
            </div>
          </Alert>
        ) : (
          <Alert variant="warning" className="d-flex align-items-start">
            <div>
              <div className="fw-bold mb-1">We couldn’t confirm your submission.</div>
              <div className="small">{status?.message || "Please try again or give us a call."}</div>
            </div>
          </Alert>
        )}

        <div className="mt-3">
          <Section title="Contact">
            <KV label="Name" value={dash(app.fullName)} />
            <KV label="Email" value={dash(app.email)} />
            <KV label="Phone" value={formatPhonePretty(app.phone)} />
          </Section>

          <Section title="Address">
            <KV label="Street" value={dash(app.address1)} />
            <KV label="Unit / Suite" value={dash(app.address2)} />
            <KV label="City / State / Zip" value={`${dash(app.city)}, ${dash(app.state)} ${dash(app.zipCode)}`} />
          </Section>

          <Section title="Energy Profile">
            <KV label="Avg. Monthly Bill" value={money(app.monthlyUtilityBill)} />
            <KV label="Pool" value={app.hasPool ? "Yes" : "No"} />
            <KV label="Hot Tub" value={app.hasHotTub ? "Yes" : "No"} />
            <KV label="EV(s)" value={app.hasEVs ? "Yes" : "No"} />
            <KV label="Other High-Usage" value={app.hasOtherHighUse ? "Yes" : "No"} />
            <KV label="Other Details" value={dash(app.otherHighUseNotes)} />
          </Section>
        </div>

        <div className="mt-4 d-flex flex-wrap gap-2">
          <Button href="tel:3033374433" variant="primary">Call {AGENT_PHONE_NUMBER}</Button>
          <Button variant="outline-secondary" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>Back to top</Button>
          <Button variant="outline-secondary" onClick={() => window.print()}>Print</Button>
        </div>
      </Card.Body>
    </Card>
  );
};

/* ---------- Tiny helpers for confirmation layout ---------- */
const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="mb-4">
    <h6 className="text-uppercase text-muted mb-2">{title}</h6>
    <Row as="dl" className="row-cols-1 row-cols-md-2 g-3 mb-0">{children}</Row>
    <hr className="mt-3 mb-0" />
  </div>
);

const KV: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Col className="mb-0">
    <dt className="small text-muted mb-1">{label}</dt>
    <dd className="mb-0">{value}</dd>
  </Col>
);

export default CERTIFIED_SOLAR_REPORT_Landing_v02O_WIZARD;
